import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Att, Attr, AttrPath } from '@cumlaude/metadata';
import { parse } from 'json-in-order';
import { difference, flatMap, fromPairs, isArray, isNil, isUndefined, last, partition, toPairs, zip } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DataTree } from './data-tree';
import { Orientation } from './export.service';
import { LegendaEntry, LegendaStyle } from '../legenda/legenda.component';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { RolHeaderService } from './rol-header.service';
import { DimKlKlas, DimVsVestiging, DimVvVestigingVak, FacBvBasisvaardigheden } from '@cumlaude/service-contract';
import { AuthService } from '@cumlaude/shared-authentication';
import { JaarMaand } from './schoolaanwezigheid';
import { TableExport } from './exportable';

export type ListOptions = {
	col: AttrPath[];
	f?: FilterExpression;
	lim?: number;
	off?: number;
};

export interface DataOptions {
	g?: AttrPath[];
	f?: FilterExpression;
	having?: FilterExpression;
	threshold?: number;
	m?: Measure[];
	r?: number[];
	xa?: number[][];
}

export interface OnderwijsresultatenDataOptions extends DataOptions {
	schooljaarThreshold?: number;
	peiljaarThreshold?: number;
}

export interface ExportFilter {
	label: any;
	value: any;
}

export interface ExportDataOptions {
	f: FilterExpression;
	attrs: Att[];
	attrLabels: string[];
	title?: string;
	showDatum: boolean;
	filters?: ExportFilter[];
	orientation: Orientation;
	legendaStyle?: LegendaStyle;
	legendaEntries?: LegendaEntry[];
}

export type PlaatsingChild = {
	ds_nm_idu: string;
	ds_is_uitstroom_extern: 0 | 1;
	ds_is_examenprognose: 0 | 1;
	ds_is_prognose: 0 | 1;
	ds_nr_weging: number;
	ds_nr_leerjaar_naar: number;
	ilt_nm_niveau: string;
	ds_nm_vestiging_naar: string;
	ds_nm_schooljaar_naar: string;
	ds_nm_opleiding_naar: string;
	ds_nm_klas_naar: string;
	ilt_nm_profiel: string;
	index?: number;
};

export type Plaatsing = {
	lb_nm_schooljaar: string;
	lb_nm_klas: string;
	lb_nr_leerjaar: number;
	vs_nm_vestiging: string;
	lb_d_plaatsing_va: string;
	lb_d_plaatsing_tm: string;
	ilt_nm_niveau: string;
	ilt_nm_profiel: string;
	lb_nm_opleiding: string;
	lb_nm_opleiding_bekostiging: string;
	lb_is_pl_voorlopig: 0 | 1;
	children: PlaatsingChild[];
};

export type DoorstroomPath = Plaatsing[];

export type SchoolaanwezigheidDetails = {
	aw_nr_leerling?: number;
	ll_fun_volledige_naam?: string;
	ll_pic_pasfoto?: string;
	aw_nm_klas?: string;
	aw_fun_nm_vak?: string;
	aw_m_perc_aanwezig?: number;
	'aw_m_perc_aanwezig-1'?: number;
	'aw_m_perc_aanwezig-2'?: number;
	'aw_m_perc_aanwezig-3'?: number;
	'aw_m_perc_aanwezig-4'?: number;
	'aw_m_perc_aanwezig-5'?: number;
	'aw_m_perc_aanwezig-6'?: number;
	'aw_m_perc_aanwezig-7'?: number;
	'aw_m_perc_aanwezig-8'?: number;
	'aw_m_perc_aanwezig-9'?: number;
	'aw_m_perc_aanwezig-10'?: number;
	'aw_m_perc_aanwezig-11'?: number;
	'aw_m_perc_aanwezig-12'?: number;
	aw_m_nr_aanwezig?: number;
	'aw_m_nr_aanwezig-1'?: number;
	'aw_m_nr_aanwezig-2'?: number;
	'aw_m_nr_aanwezig-3'?: number;
	'aw_m_nr_aanwezig-4'?: number;
	'aw_m_nr_aanwezig-5'?: number;
	'aw_m_nr_aanwezig-6'?: number;
	'aw_m_nr_aanwezig-7'?: number;
	'aw_m_nr_aanwezig-8'?: number;
	'aw_m_nr_aanwezig-9'?: number;
	'aw_m_nr_aanwezig-10'?: number;
	'aw_m_nr_aanwezig-11'?: number;
	'aw_m_nr_aanwezig-12'?: number;
	aw_m_nr_gemist?: number;
	'aw_m_nr_gemist-1'?: number;
	'aw_m_nr_gemist-2'?: number;
	'aw_m_nr_gemist-3'?: number;
	'aw_m_nr_gemist-4'?: number;
	'aw_m_nr_gemist-5'?: number;
	'aw_m_nr_gemist-6'?: number;
	'aw_m_nr_gemist-7'?: number;
	'aw_m_nr_gemist-8'?: number;
	'aw_m_nr_gemist-9'?: number;
	'aw_m_nr_gemist-10'?: number;
	'aw_m_nr_gemist-11'?: number;
	'aw_m_nr_gemist-12'?: number;
	aw_m_nr_lesuren?: number;
	'aw_m_nr_lesuren-1'?: number;
	'aw_m_nr_lesuren-2'?: number;
	'aw_m_nr_lesuren-3'?: number;
	'aw_m_nr_lesuren-4'?: number;
	'aw_m_nr_lesuren-5'?: number;
	'aw_m_nr_lesuren-6'?: number;
	'aw_m_nr_lesuren-7'?: number;
	'aw_m_nr_lesuren-8'?: number;
	'aw_m_nr_lesuren-9'?: number;
	'aw_m_nr_lesuren-10'?: number;
	'aw_m_nr_lesuren-11'?: number;
	'aw_m_nr_lesuren-12'?: number;
	aw_m_nr_lesuren_aanwezig?: number;
	'aw_m_nr_lesuren_aanwezig-1'?: number;
	'aw_m_nr_lesuren_aanwezig-2'?: number;
	'aw_m_nr_lesuren_aanwezig-3'?: number;
	'aw_m_nr_lesuren_aanwezig-4'?: number;
	'aw_m_nr_lesuren_aanwezig-5'?: number;
	'aw_m_nr_lesuren_aanwezig-6'?: number;
	'aw_m_nr_lesuren_aanwezig-7'?: number;
	'aw_m_nr_lesuren_aanwezig-8'?: number;
	'aw_m_nr_lesuren_aanwezig-9'?: number;
	'aw_m_nr_lesuren_aanwezig-10'?: number;
	'aw_m_nr_lesuren_aanwezig-11'?: number;
	'aw_m_nr_lesuren_aanwezig-12'?: number;
};

@Injectable({
	providedIn: 'root',
})
export class DataService {
	private readonly http = inject(HttpClient);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	protected readonly authService = inject(AuthService);
	private readonly rolHeaderService = inject(RolHeaderService);

	getLeerlingBasisvaardigheden(nr_leerling: number, vaardigheid: string): Observable<FacBvBasisvaardigheden[]> {
		const options: ListOptions = {
			f: new CompoundFilterExpression([
				new BasicFilterExpression(['bv_nr_leerling'], nr_leerling),
				new BasicFilterExpression(['bv_nm_vaardigheid'], vaardigheid),
			]),
			col: [
				['bv_nm_toetsmoment'],
				['bv_d_afname'],
				['bv_pk_key'], // zodat ze gesorteerd worden op bv_fun_sortkey_afname: (bv_d_afname,bv_pk_key)
				['bv_nm_leerfase'],
				['bv_nr_referentieniveau'],
				['bv_nm_referentieniveau'],
				['bv_nm_schooljaar'],
			],
		};
		return this.getList<FacBvBasisvaardigheden>('basisvaardigheden-details/list', options).pipe(map((table) => table.rows));
	}

	getLeerlingen<T>(options: Partial<ListOptions>): Observable<TableResponse<T>> {
		return this.getList<T>('leerling/list', options);
	}

	getMedewerkers<T>(options: Partial<ListOptions>): Observable<TableResponse<T>> {
		return this.getList<T>('medewerker/list', options);
	}

	getBeheerMedewerkersLijst<T>(): Observable<TableResponse<T>> {
		return this.getList('medewerker/beheerlist', {});
	}

	getVestigingen(options?: Partial<ListOptions>): Observable<DimVsVestiging[]> {
		return this.getList<DimVsVestiging>('vestiging/list', {
			...options,
			col: [
				['vs_id_vestiging'],
				['vs_is_actief'],
				['vs_is_vo'],
				['vs_is_vso'],
				['vs_nm_vestiging'],
				['vs_cos_brinvest'],
				['vs_cos_brinvest_actief'],
			],
		}).pipe(map((table) => table.rows));
	}

	getKlassen(): Observable<DimKlKlas[]> {
		const col: AttrPath[] = [['kl_nm_klas'], ['kl_id_vestiging']];
		return this.getList<DimKlKlas>('klas/list', { col }).pipe(map((table) => table.rows));
	}

	getVestigingVakken(): Observable<DimVvVestigingVak[]> {
		const col: AttrPath[] = [['vv_nm_vak'], ['vv_abb_vak'], ['vv_id_vestiging']];
		return this.getList<DimVvVestigingVak>('vestiging_vak/list', { col }).pipe(map((table) => table.rows));
	}

	getList<T>(endpoint: string, options: Partial<ListOptions>): Observable<TableResponse<T>> {
		const url = `${this.envConfig.dataUrl}/${endpoint}`;
		const { col, f, lim, off } = options;
		const filterOptions = this.createFilterOption(f);

		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => {
				const params = {
					...(col ? { col: col.map((p) => p.join('.')) } : {}),
					...filterOptions,
					...(lim ? { lim } : {}),
					...(off ? { off } : {}),
				};
				return this.http
					.get(url, {
						params,
						headers: this.rolHeaderService.getActiveRolHeader(),
						observe: 'body',
						responseType: 'text',
					})
					.pipe(map(parseTableJson), tap(logResponseQueries<TableResponse<T>>));
			})
		);
	}

	getLeerlingDetails<T>(col: Attr[][], key?: number): Observable<TableResponse<T>> {
		return this.getDetails<T>('loopbaan/leerlingdetails', col, key);
	}

	getMedewerkerDetails<T>(col: Attr[][], key?: number): Observable<TableResponse<T>> {
		return this.getDetails<T>('medewerker/details', col, key);
	}

	getDetails<T>(endpoint: string, col: Attr[][], key?: number): Observable<TableResponse<T>> {
		const url = `${this.envConfig.dataUrl}/${endpoint}`;
		const params = {
			col: col.map((p) => p.join('.')),
			key: JSON.stringify(key),
		};
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.get(url, {
					params,
					headers: this.rolHeaderService.getActiveRolHeader(),
					observe: 'body',
					responseType: 'text',
				})
			),
			map(parseTableJson),
			tap(logResponseQueries<TableResponse<T>>)
		);
	}

	getDoorstroomDetails(nr_leerling: number): Observable<DoorstroomPath[]> {
		const url = `${this.envConfig.dataUrl}/doorstroom/details/${nr_leerling}`;
		return this.http.get<DoorstroomPath[]>(url, { headers: this.rolHeaderService.getActiveRolHeader() });
	}

	getData(endpoint: string, options: DataOptions & OnderwijsresultatenDataOptions): Observable<DataResponse<number[]>> {
		const filterOptions = this.createFilterOption(options.f);
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => {
				// Dashboards willen vaak een rollup op row level. Als dit het diepste level is, is dit eigenlijk niet nodig, dus laten we die weg.
				// (De backend faalt erop).
				const r: { r?: number[] } = options.g && options.r ? { r: options.r.filter((i) => i != options.g!.length) } : {};
				const params = {
					...(options.g ? { g: options.g.map((p) => p.join('.')) } : {}),
					...r,
					...(options.xa ? { xa: options.xa.map((v) => JSON.stringify(v)) } : {}),
					...filterOptions,
					...(options.having ? { having: JSON.stringify(options.having) } : {}),
					...(options.threshold ? { threshold: options.threshold } : {}),
					...(options.peiljaarThreshold ? { peiljaarThreshold: options.peiljaarThreshold } : {}),
					...(options.schooljaarThreshold ? { schooljaarThreshold: options.schooljaarThreshold } : {}),
					...(options.m ? { m: options.m } : {}),
				};
				const url = `${this.envConfig.dataUrl}/${endpoint}`;
				return this.http.get(url, {
					headers: { Accept: 'text/json', ...this.rolHeaderService.getActiveRolHeader() },
					params,
					observe: 'body',
					responseType: 'text',
				});
			}),
			map(parseTreeJson),
			tap(logResponseQueries<DataResponse<number[]>>)
		);
	}

	private createFilterOption(expression?: FilterExpression): { f: string } | {} {
		return expression ? { f: JSON.stringify(expression) } : {};
	}

	getExportData(endpoint: string, options: ExportDataOptions): Observable<Blob> {
		const filterOptions = this.createFilterOption(options.f);
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => {
				const params = {
					...filterOptions,
					attrs: options.attrs,
					attrLabels: options.attrLabels,
					...(options.title ? { title: options.title } : {}),
					showDatum: options.showDatum,
					...(options.filters ? { filters: JSON.stringify({ filters: options.filters }) } : {}),
				};
				const url = `${this.envConfig.dataUrl}/${endpoint}/exportdata`;
				return this.http.get(url, {
					headers: {
						Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
						...this.rolHeaderService.getActiveRolHeader(),
					},
					params,
					observe: 'body',
					responseType: 'blob',
				});
			})
		);
	}

	getTableExport(data: TableExport<any>): Observable<Blob> {
		const url = `${this.envConfig.dataUrl}/tableexport`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.post(url, data, {
					headers: {
						Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
						'Content-Type': 'application/json',
						...this.rolHeaderService.getActiveRolHeader(),
					},
					observe: 'body',
					responseType: 'blob',
				})
			)
		);
	}

	/**
	 * Gaat ervan uit dat options.f een CompoundFilterExpression is.
	 * Geeft een nieuwe {f,having} terug waarbij eventuele filters (een BasicFilterExpression) op alle meegegeven attrs uit de f
	 * zijn verplaatst naar having.
	 */
	moveToHaving(attrs: AttrPath[], options: DataOptions): { f?: FilterExpression; having?: FilterExpression } {
		const compound = options.f!;

		const [having, f] = this.partitionByFilterAttr(compound, attrs)
			.map((filters) => filters.filter((f) => !f.isEmpty()))
			.map((filters) => (filters.length ? new CompoundFilterExpression(filters) : undefined));
		return { f, having };
	}

	partitionByFilterAttr(compound: FilterExpression, attrs: AttrPath[]): [FilterExpression[], FilterExpression[]] {
		if (!(compound instanceof CompoundFilterExpression))
			throw new Error(`Expected: CompoundFilterExpression. Actual: ${JSON.stringify(compound)}`);
		const atts = attrs.map((attr) => attr.join('.'));
		return partition(compound.filters, (filter) => atts.includes(<Att>asBasicFilterExpression(filter)?.attr.join('.')));
	}

	getFilterVal(compound: FilterExpression, attr: AttrPath): string | undefined {
		const [target] = this.partitionByFilterAttr(compound, [attr]);
		return target[0]?.getValueString();
	}

	getAanwezigheidData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('aanwezigheid', options);
	}

	getAanwezigheidExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('aanwezigheid', options);
	}

	getBasisvaardighedenData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('basisvaardigheden', options);
	}

	getBasisvaardighedenExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('basisvaardigheden', options);
	}

	getCijferkolommenData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('cijferkolommen', options);
	}

	getCijfersData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('cijfers', options);
	}

	getCijfersExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('cijfers', options);
	}

	getDoorstroomData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('doorstroom', options);
	}

	getDoorstroomCohortdetailsGraphData(options: { f?: FilterExpression }): Observable<DataResponse<number[]>> {
		return this.getData('cohortdetails/graph', options);
	}

	getDoorstroomCohortdetailsExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('cohortdetails', options);
	}

	getDoorstroomExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('doorstroom', options);
	}

	getExamencijfersData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('examencijfers', options);
	}

	getExamencijfersExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('examencijfers', options);
	}

	getLeerfaseData(): Observable<DataResponse<number[]>> {
		return this.getData('leerfase', {});
	}

	getLesregistratieData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('lesregistratie', options);
	}

	getLesregistratieExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('lesregistratie', options);
	}

	getOnderwijsresultatenData(options: OnderwijsresultatenDataOptions): Observable<DataResponse<number[]>> {
		return this.getData('onderwijsresultaten', options);
	}

	getOnderwijsresultatenExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('onderwijsresultaten', options);
	}

	getPrestatieanalyseVsoData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('prestatieanalyse_vso', options);
	}

	getSchoolAanwezigheidData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('schoolaanwezigheid', options);
	}

	getSchoolAanwezigheidDetails(options: {
		g: AttrPath;
		f?: FilterExpression;
		laagFilter?: FilterExpression;
		month: JaarMaand[];
		sortmonth?: JaarMaand;
		sortdir: 'asc' | 'desc';
		lim?: number;
		off?: number;
	}): Observable<TableResponse<SchoolaanwezigheidDetails>> {
		const url = `${this.envConfig.dataUrl}/schoolaanwezigheid/details`;
		const { g, f, laagFilter, month, sortmonth, sortdir, lim, off } = options;
		const filterOptions = this.createFilterOption(f);

		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => {
				const params = {
					g: g.join('.'),
					...filterOptions,
					...(laagFilter ? { laagfilter: JSON.stringify(laagFilter) } : {}),
					...(lim ? { lim } : {}),
					...(off ? { off } : {}),
					month: month.map(encodeJaarMaand),
					...(sortmonth ? { sortmonth: encodeJaarMaand(sortmonth) } : {}),
					sortdir,
				};
				return this.http
					.get(url, {
						params,
						headers: this.rolHeaderService.getActiveRolHeader(),
						observe: 'body',
						responseType: 'text',
					})
					.pipe(map(parseTableJson), tap(logResponseQueries<TableResponse<SchoolaanwezigheidDetails>>));
			})
		);
	}

	getVakkeuzeData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.getData('vakkeuze', options);
	}

	getVakkeuzeExportData(options: ExportDataOptions): Observable<Blob> {
		return this.getExportData('vakkeuze', options);
	}

	fetchFirstFilterValue<T>(endpoint: string, attr: AttrPath, otherFilters: FilterExpression[] = []): Observable<T | undefined> {
		return this.getActiveFilterValues(endpoint, attr, otherFilters, []).pipe(
			map((resp) => resp.values[0]),
			catchError((_err) => {
				return of(undefined);
			})
		);
	}

	fetchFirstFilterValueAsArray<T>(endpoint: string, attr: AttrPath, otherFilters: FilterExpression[] = []): Observable<T[] | undefined> {
		return this.fetchFirstFilterValue<T>(endpoint, attr, otherFilters).pipe(
			map((value: T | undefined) => (!isUndefined(value) ? [value] : undefined))
		);
	}

	getFilterValues(
		endpoint: string,
		target: AttrPath,
		otherFilters: FilterExpression[],
		permanentFilters: FilterExpression[]
	): Observable<FilterData> {
		const activeFilterValues$ = this.getActiveFilterValues(endpoint, target, otherFilters, permanentFilters);
		const allFilterValues$ = this.getAllFilterValues(endpoint, target, permanentFilters);
		return combineLatest([activeFilterValues$, allFilterValues$]).pipe(
			map(([activeFV, allFV]) => ({
				attr: activeFV.attr,
				allValues: allFV.values,
				activeValues: activeFV.values,
				inactiveValues: difference(allFV.values, activeFV.values),
			}))
		);
	}

	getActiveFilterValues(
		endpoint: string,
		target: AttrPath,
		otherFilters: FilterExpression[],
		permanentFilters: FilterExpression[]
	): Observable<FilterValues> {
		const url = `${this.envConfig.dataUrl}${endpoint}/activefiltervalues`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap((_) => {
				const params = {
					target: target.join('.'),
					of: otherFilters.map((f) => JSON.stringify(f)),
					pf: JSON.stringify(new CompoundFilterExpression(permanentFilters)),
				};
				return this.http.get<FilterValues>(url, { params, headers: this.rolHeaderService.getActiveRolHeader() });
			})
		);
	}

	getAllFilterValues(endpoint: string, target: AttrPath, permanentFilters: FilterExpression[]): Observable<FilterValues> {
		const url = `${this.envConfig.dataUrl}${endpoint}/allfiltervalues`;
		const params = {
			target: target.join('.'),
			pf: JSON.stringify(new CompoundFilterExpression(permanentFilters)),
		};
		return this.authService
			.refreshTokenIfNeeded()
			.pipe(switchMap(() => this.http.get<FilterValues>(url, { params, headers: this.rolHeaderService.getActiveRolHeader() })));
	}
}

function encodeJaarMaand(jm: JaarMaand) {
	return `${jm.nr_jaar}-${jm.nr_maand.padStart(2, '0')}`;
}

export interface DataResponse<D> extends QueryResponse {
	g: AttrPath[];
	measures: string[];
	data: DataTree<D>;
	order?: DataTree<[]>;
	metadata?: Map<string, string>;
}

export interface TableResponse<R> extends QueryResponse {
	columns: string[];
	rows: R[];
	total: number;
}

export interface QueryResponse {
	queries: string[];
}

export interface FilterData {
	attr: string[];
	allValues: any[];
	activeValues: any[];
	inactiveValues: any[];
}

export interface FilterValues {
	attr: string[];
	values: any[];
}

export interface FilterExpression {
	getValueString(): string;

	isEmpty(): boolean;
}

export class CompoundFilterExpression implements FilterExpression {
	constructor(
		public filters: FilterExpression[],
		public type: 'and' | 'or' = 'and'
	) {}

	isEmpty = () => this.type === 'and' && this.filters.length === 0;

	getValueString(): string {
		return this.filters.map((f) => f.getValueString()).join(` ${this.type} `);
	}
}

export type BasicFilterExpressionType = '=' | '>' | '<' | '<=' | '>=' | '<>' | 'in' | 'not in' | 'any' | 'like' | 'like_unaccent';

export function isQueryFilterExpression(fex: FilterExpression | QueryFilterExpression): fex is QueryFilterExpression {
	if (!('type' in fex)) return false;

	return fex.type === '?';
}

export class QueryFilterExpression implements FilterExpression {
	type = '?';

	constructor(public attr: AttrPath) {}

	isEmpty = () => true;

	getValueString(): string {
		return '';
	}
}

export class BasicFilterExpression<T> implements FilterExpression {
	constructor(
		public attr: AttrPath,
		public val: T | null = null,
		public type: BasicFilterExpressionType = '=',
		public otherAttr: AttrPath | undefined = undefined
	) {}

	isEmpty = () => isUndefined(this.val);

	getValueString(): string {
		return this.val ? `${this.val}` : '';
	}
}

export class BetweenFilterExpression<T> implements FilterExpression {
	constructor(
		public attr: AttrPath,
		public from: T | null,
		public to: T | null
	) {}

	type = 'between';

	isEmpty = () => isNil(this.from) || isNil(this.to);

	getValueString(): string {
		return !this.isEmpty() ? `between ${this.from} and ${this.to}` : '';
	}
}

export class FilterStaticSelectionExpression implements FilterExpression {
	constructor(public values: number[]) {}

	type = 'in_static';

	isEmpty = () => false;

	getValueString(): string {
		return `selection:${this.values.join(',')}`;
	}
}

export class FilterDynamicSelectionExpression implements FilterExpression {
	constructor(
		public sp: string,
		public sf: FilterExpression
	) {}

	type = 'in_dynamic';

	isEmpty = () => false;

	getValueString(): string {
		return `${this.sp} selection: ${this.sf.getValueString()}`;
	}
}

export class InFilterExpression<T extends Array<any>> extends BasicFilterExpression<T> {
	constructor(
		public override attr: AttrPath,
		public override val: T | null = null
	) {
		super(attr, val, 'in');
	}

	override isEmpty = () => isNil(this.val) || this.val.length === 0;

	override getValueString(): string {
		return this.val ? this.val.map((v) => v.toString()).join(', ') : '';
	}
}

export function isBasicFilterExpression(filter: FilterExpression): filter is BasicFilterExpression<any> {
	return filter instanceof BasicFilterExpression;
}

/**
 * Deserializes a plain JSON object (as produced by JSON.stringify on a FilterExpression) back into
 * the appropriate FilterExpression subclass instance.
 *
 * Dispatch logic based on the `type` field:
 *   - 'and' | 'or'        → CompoundFilterExpression  (recursively deserializes children)
 *   - '?'                 → QueryFilterExpression
 *   - 'between'           → BetweenFilterExpression
 *   - 'in_static'         → FilterStaticSelectionExpression
 *   - 'in_dynamic'        → FilterDynamicSelectionExpression (recursively deserializes `sf`)
 *   - 'in'                → InFilterExpression
 *   - '=' | '<>' | …      → BasicFilterExpression
 *   - (no type field)     → CompoundFilterExpression with default 'and'
 */
export function deserializeFilterExpression(stringified: string): FilterExpression {
	return hydrateFilterExpression(JSON.parse(stringified));
}

function hydrateFilterExpression(raw: any): FilterExpression {
	switch (raw.type as string) {
		case 'and':
		case 'or': {
			const filters: FilterExpression[] = (raw.filters ?? []).map(hydrateFilterExpression);
			return new CompoundFilterExpression(filters, raw.type);
		}
		case '?':
			return new QueryFilterExpression(raw.attr);
		case 'between':
			return new BetweenFilterExpression(raw.attr, raw.from, raw.to);
		case 'in_static':
			return new FilterStaticSelectionExpression(raw.values);
		case 'in_dynamic':
			return new FilterDynamicSelectionExpression(raw.sp, hydrateFilterExpression(raw.sf));
		case 'in':
			return new InFilterExpression(raw.attr, raw.val);
		default:
			// Covers '=', '>', '<', '<=', '>=', '<>', 'not in', 'any', 'like', 'like_unaccent'
			return new BasicFilterExpression(raw.attr, raw.val, raw.type as BasicFilterExpressionType, raw.otherAttr);
	}
}

/** Keys in de geserialiseerde vorm die een attribuutpad bevatten. */
const ATTR_KEYS = ['attr', 'otherAttr'];

/**
 * Geeft een kopie van de expressie waarin elk attribuutpad door `map` is gehaald: bedoeld om een
 * filter "om te hangen" naar een ander attribuut. Geef hetzelfde pad terug om een filter te laten
 * staan. Leerlingselecties blijven hierbij buiten schot omdat de daarin gebruikte attributen de
 * de selectie definiëren.
 *
 * Werkt op de geserialiseerde vorm en hydrateert daarna, zodat hier geen kennis van de subklassen
 * nodig is. hydrateFilterExpression is de enige plek die de subklassen kent, en die moet compleet
 * blijven omdat elk filter als JSON naar de backend gaat - een nieuwe subklasse die daar niet in staat,
 * werkt überhaupt niet.
 */
export function mapFilterAttrs(filter: FilterExpression, map: (attr: AttrPath) => AttrPath): FilterExpression {
	return hydrateFilterExpression(mapAttrsInRaw(JSON.parse(JSON.stringify(filter)), map));
}

function mapAttrsInRaw(raw: any, map: (attr: AttrPath) => AttrPath): any {
	if (isArray(raw)) return raw.map((value) => mapAttrsInRaw(value, map));
	if (raw === null || typeof raw !== 'object') return raw;

	// Dynamische selecties niet omhangen. Een statische selectie (in_static) bevat alleen ids en heeft niets om te hangen.
	if (raw.type === 'in_dynamic') return raw;

	return fromPairs(
		toPairs(raw).map(([key, value]) => [key, ATTR_KEYS.includes(key) && isAttrPath(value) ? map(value) : mapAttrsInRaw(value, map)])
	);
}

function isAttrPath(value: any): value is AttrPath {
	return isArray(value) && value.every((part) => typeof part === 'string');
}

function isInFilterExpression(filter: FilterExpression): filter is InFilterExpression<any> {
	return filter instanceof InFilterExpression;
}

export function asBasicFilterExpression(filter: FilterExpression): BasicFilterExpression<any> | undefined {
	return isBasicFilterExpression(filter) ? filter : undefined;
}

/**
 * NB houdt geen rekening met geneste and/or combinaties, slaat gewoon alles plat
 */
export function flattenFilters(filter: FilterExpression | FilterExpression[]): (BasicFilterExpression<any> | InFilterExpression<any>)[] {
	if (isArray(filter)) return flatMap(filter.map((sub) => flattenFilters(sub)));

	if (isCompoundFilterExpression(filter)) return flattenFilters(filter.filters);
	else if (isDynamicSelectionExpression(filter)) return flattenFilters(filter.sf);
	else if (isBasicFilterExpression(filter) || isInFilterExpression(filter)) return [filter];
	return [];
}

export function extractDynamicSelectionExpressions(filter: FilterExpression): [FilterDynamicSelectionExpression[], FilterExpression | undefined] {
	if (isDynamicSelectionExpression(filter)) {
		return [[filter], undefined];
	}

	if (isCompoundFilterExpression(filter)) {
		// Only recurse into 'and' compound filters
		if (filter.type === 'and' || (filter.type === 'or' && filter.filters.length === 1)) {
			const allExtracted: FilterDynamicSelectionExpression[] = [];
			const processedFilters: FilterExpression[] = [];

			for (const sub of filter.filters) {
				const [extracted, altered] = extractDynamicSelectionExpressions(sub);
				allExtracted.push(...extracted);
				if (altered !== undefined) {
					processedFilters.push(altered);
				}
			}

			// Return a new CompoundFilterExpression with the processed filters
			let alteredFilter: FilterExpression;
			if (processedFilters.length === 0) {
				alteredFilter = new CompoundFilterExpression([], 'and');
			} else if (processedFilters.length === 1) {
				alteredFilter = processedFilters[0];
			} else {
				alteredFilter = new CompoundFilterExpression(processedFilters, 'and');
			}

			return [allExtracted, alteredFilter];
		}

		// For 'or' expressions, return as-is without recursing
		return [[], filter];
	}

	// For other filter types, return as-is
	return [[], filter];
}

function isCompoundFilterExpression(filter: FilterExpression): filter is CompoundFilterExpression {
	return 'type' in filter && ['and', 'or'].includes(filter.type as string);
}

function isDynamicSelectionExpression(filter: FilterExpression): filter is FilterDynamicSelectionExpression {
	return 'type' in filter && filter.type === 'in_dynamic';
}

function parseTableJson(input: string): TableResponse<any> {
	const res = parse(input) as Map<string, any>;
	const columns = (res.get('columns') as AttrPath[]).map((col) => last(col)!);
	const data = <any[]>res.get('data');
	const rows = data.map((row) => fromPairs(zip(columns, row)));
	const total = res.get('total');
	const queries = res.get('queries');
	return { columns, rows, total, queries };
}

function logResponseQueries<T extends QueryResponse>(response: T) {
	if (!response.queries) return;

	response.queries.forEach((query) => console.log(query));
}

export function parseTreeJson(input: string): DataResponse<number[]> {
	const res = parse(input) as Map<string, any>;
	return {
		g: res.get('g'),
		measures: res.get('measures'),
		data: res.get('data'),
		order: res.get('order'),
		metadata: res.get('metadata'),
		queries: res.get('queries'),
	};
}

export enum CijferMeasure {
	HISTOGRAM = 'histogram',
	RELATIE = 'relatie',
	PLAATSING_ADVIES = 'plaatsing_advies',
	STATS = 'stats',
	SE_CE = 'se_ce',
	SE = 'se',
	CE = 'ce',
	LABEL = 'label',
	TOETS = 'toets',
	BIJZONDERHEID = 'bijzonderheid',
	AANWEZIGHEID = 'aanwezigheid',
}

export enum AanwezigheidMeasure {
	LESUREN = 'lesuren',
	LESTIJD = 'lestijd',
	LESDAGEN = 'lesdagen',
	LEERLINGEN = 'leerlingen',
	LESSEN = 'lessen',
}

export enum DoorstroomMeasure {
	LEERLINGEN = 'leerlingen',
	DOORSTROOM_AANTALLEN = 'doorstroom_aantallen',
	GESLAAGD_AANTALLEN = 'geslaagd_aantallen',
	COHORTRENDEMENT_TOT = 'cohortrendement_tot',
	COHORTRENDEMENT_VANAF = 'cohortrendement_vanaf',
	EXAMENRENDEMENT = 'examenrendement',
	OPSTROOMRENDEMENT = 'opstrooomendement',
}

type Measure = CijferMeasure | DoorstroomMeasure | AanwezigheidMeasure;

export const defaultDoorstroomAvailableGroups: AttrPath[] = [
	['ds_fk_ll', 'll_nm_basisschooladvies'],
	['ds_nm_bekostigingstype_van'],
	['ds_co_brin_svh'],
	['ds_fk_ll', 'll_nm_geslacht'],
	['ds_fk_ll', 'll_nm_nationaliteit1'],
	['ds_fk_ll', 'll_nm_nationaliteit2'],
	['ds_fk_ilt_van', 'ilt_nm_niveau'],
	['ds_fk_ll', 'll_nm_schoolsoort_svh'],
	['ds_nm_klas_van'],
	['ds_nr_leerjaar_van'],
	['ds_nm_opleiding_van'],
	['ds_fk_ilt_van', 'ilt_nm_opleiding'],
	['ds_fk_ll', 'll_nr_pc4'],
	['ds_fk_ilt_van', 'ilt_abb_profiel'],
	['ds_fk_ll', 'll_nm_svh'],
	['ds_fk_vs_van', 'vs_nm_vestiging'],
	['ds_fk_ll', 'll_nm_woonplaats'],
];

/**
 * Produceert een lijstje voor de `xa` parameter dat staat voor een aggregatie over alle `groups`
 * behalve `target` (ofwel een GROUP BY over alleen `target`).
 */
export function xAggExcept(groups: AttrPath[], target: AttrPath): number[] {
	return groups.flatMap((group, ix) => (group.join('.') === target.join('.') ? [] : [ix]));
}
